import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { Fragment, type ReactNode } from 'react';

import { cn, cva, type VariantProps } from '@lib/utils';
import { InputErrorMsg } from './InputErrorMsg';

// ----------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DataItem = Record<string, any>;

const listboxButtonVariants = cva(
  [
    // Layout
    'flex w-full items-center justify-between cursor-pointer',
    // Typography
    'text-fg placeholder:font-light placeholder:text-fg-muted',
    // Border & Background
    'rounded-lg border bg-transparent',
    // Focus
    'outline-hidden focus:outline-hidden',
    // Transitions
    'transition-colors duration-200 ease-in-out',
    // Primary context for focus states
    'this:primary',
  ],
  {
    variants: {
      size: {
        xs: 'h-7 text-xs px-2 py-1',
        sm: 'h-8 text-sm px-2.5 py-1.5',
        md: 'h-10 text-sm px-3 py-2',
        lg: 'h-11 text-base px-3.5 py-2.5',
        xl: 'h-12 text-base px-4 py-3',
      },
      open: {
        true: 'border-this-solid',
        false: 'border-line hover:border-line-hover data-[focus]:border-this-solid',
      },
      hasError: {
        true: 'this:error border-this-solid hover:border-this-solid',
        false: '',
      },
      disabled: {
        true: 'cursor-not-allowed opacity-60 bg-surface-raised border-line',
        false: '',
      },
    },
    defaultVariants: {
      size: 'md',
      open: false,
      hasError: false,
      disabled: false,
    },
  }
);

const listboxOptionsVariants = cva([
  // Layout
  'absolute z-100 max-h-60 w-(--button-width) overflow-auto py-1',
  // Border & Background
  'rounded-lg border border-line bg-canvas',
  // Shadow
  'shadow-lg shadow-surface/50 dark:shadow-none',
  // Focus
  'outline-hidden focus-visible:outline-hidden',
]);

const listboxOptionVariants = cva(
  [
    // Layout
    'relative cursor-pointer py-2 pe-10 ps-4 select-none',
    // Focus
    'outline-hidden',
    // Transitions
    'transition-colors',
  ],
  {
    variants: {
      selected: {
        true: 'bg-this-solid text-white this:primary',
        false: 'text-fg',
      },
      active: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        active: true,
        selected: false,
        className: 'bg-ui',
      },
    ],
    defaultVariants: {
      selected: false,
      active: false,
    },
  }
);

const labelVariants = cva(['text-sm font-medium text-fg']);

/** State passed to renderOption */
export interface OptionRenderState {
  selected: boolean;
  active: boolean;
}

/** Props for custom option rendering */
export type RenderOptionFn<T> = (item: T, state: OptionRenderState) => ReactNode;

/** Props for custom value rendering in button */
export type RenderValueFn<T> = (value: T | T[] | null) => ReactNode;

type BaseListBoxProps<T extends DataItem = DataItem> = {
  data: T[];
  placeholder?: string;
  label?: ReactNode;
  displayField?: string;
  error?: boolean | string;
  disabled?: boolean;
  name?: string;
  by?: keyof T | ((a: T, b: T) => boolean);
  inputProps?: Record<string, unknown>;
  rootProps?: Record<string, unknown>;
  className?: string;
  classNames?: {
    root?: string;
    listbox?: string;
    button?: string;
    options?: string;
    option?: string;
    label?: string;
  };
  /** Custom render function for dropdown options */
  renderOption?: RenderOptionFn<T>;
  /** Custom render function for the selected value in the button */
  renderValue?: RenderValueFn<T>;
} & Omit<VariantProps<typeof listboxButtonVariants>, 'hasError' | 'disabled'>;

type SingleListBoxProps<T extends DataItem = DataItem> = BaseListBoxProps<T> & {
  multiple?: false;
  value?: T | null;
  defaultValue?: T;
  onChange?: (value: T) => void;
};

type MultipleListBoxProps<T extends DataItem = DataItem> = BaseListBoxProps<T> & {
  multiple: true;
  value?: T[];
  defaultValue?: T[];
  onChange?: (value: T[]) => void;
};

type ListBoxProps<T extends DataItem = DataItem> = SingleListBoxProps<T> | MultipleListBoxProps<T>;

type ListBoxOption = DataItem;

function ListBox<T extends DataItem = DataItem>(props: ListBoxProps<T>) {
  const {
    data,
    value,
    defaultValue,
    onChange,
    placeholder,
    label,
    displayField = 'label',
    error,
    multiple,
    disabled = false,
    name,
    by,
    inputProps,
    rootProps,
    className,
    classNames = {},
    size = 'md',
    renderOption,
    renderValue,
  } = props;

  const hasError = !!error;

  return (
    <div
      className={cn('flex flex-col [&_.suffix]:pointer-events-none', classNames.root)}
      {...rootProps}
    >
      <Listbox
        as="div"
        className={cn(className, classNames.listbox)}
        value={value}
        defaultValue={defaultValue}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={onChange as any}
        multiple={multiple}
        by={by as string}
        disabled={disabled}
        name={name}
      >
        {({ open, value: selectedValue }: { open: boolean; value: T | T[] | null }) => (
          <>
            {label && <Label className={cn(labelVariants(), classNames.label)}>{label}</Label>}

            <div className={cn('relative', label && 'mt-1.5')}>
              <ListboxButton
                className={({ focus }: { focus: boolean }) =>
                  cn(
                    listboxButtonVariants({ size, open: open || focus, hasError, disabled }),
                    classNames.button
                  )
                }
                {...inputProps}
              >
                {({ focus }: { focus: boolean }) => (
                  <>
                    {renderValue ? (
                      <span className="block truncate">{renderValue(selectedValue)}</span>
                    ) : (
                      <span className="block truncate">
                        <span className="text-fg-muted">
                          {!(
                            (selectedValue as T)?.[displayField] || (selectedValue as T[])?.length
                          ) && placeholder}
                        </span>
                        <span>
                          {multiple
                            ? (selectedValue as T[])?.map((item) => item[displayField]).join(', ')
                            : (selectedValue as T)?.[displayField]}
                        </span>
                      </span>
                    )}
                    <ChevronDownIcon
                      className={cn(
                        'size-5 shrink-0 transition',
                        open || focus ? 'text-this-solid' : 'text-fg-muted',
                        open && 'rotate-180'
                      )}
                    />
                  </>
                )}
              </ListboxButton>
              <Transition
                as={Fragment}
                enter="transition ease-out"
                enterFrom="opacity-0 translate-y-2"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-2"
              >
                <ListboxOptions
                  anchor={{ to: 'bottom end', gap: 8 }}
                  className={cn(listboxOptionsVariants(), classNames.options)}
                >
                  {data.map((item: T, i: number) => (
                    <ListboxOption
                      key={i}
                      className={({ selected, focus }: { selected: boolean; focus: boolean }) =>
                        cn(
                          listboxOptionVariants({ selected, active: focus }),
                          renderOption && 'ps-3 pe-3', // Tighter padding for custom renders
                          classNames.option
                        )
                      }
                      value={item}
                    >
                      {({ selected, focus }: { selected: boolean; focus: boolean }) => (
                        <>
                          {renderOption ? (
                            renderOption(item, { selected, active: focus })
                          ) : (
                            <>
                              <span className="block truncate">{item[displayField]}</span>

                              {selected ? (
                                <span className="absolute inset-y-0 flex items-center end-0 pe-3">
                                  <CheckIcon className="size-5" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
      <InputErrorMsg when={hasError && typeof error !== 'boolean'}>{error}</InputErrorMsg>
    </div>
  );
}

ListBox.displayName = 'ListBox';

export { ListBox, listboxButtonVariants };
export type { ListBoxProps, ListBoxOption };
